import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

// ── Pricing tiers (locked May 2026) ──────────────────────────────────────────
const TIER_BANDS = [
  { name: 'Solo',       min: 1,   max: 5,   rates: { USD: 395, AUD: 599, GBP: 299 } },
  { name: 'Group',      min: 6,   max: 20,  rates: { USD: 295, AUD: 449, GBP: 229 } },
  { name: 'Regional',   min: 21,  max: 50,  rates: { USD: 225, AUD: 349, GBP: 179 } },
  { name: 'National',   min: 51,  max: 150, rates: { USD: 165, AUD: 249, GBP: 129 } },
  { name: 'Enterprise', min: 151, max: 300, rates: { USD: 119, AUD: 189, GBP: 95 } },
  { name: 'Strategic',  min: 301, max: null, rates: { USD: 89,  AUD: 139, GBP: 69 } },
];

const CURRENCY_SYMBOL = { USD: '$', AUD: 'A$', GBP: '£' };

function getTier(qty) {
  return TIER_BANDS.find(b => qty >= b.min && (b.max === null || qty <= b.max))
    || TIER_BANDS[TIER_BANDS.length - 1];
}

function calcMonthlyBill(qty, currency, period) {
  const tier = getTier(qty);
  const rate = (tier.rates[currency] || tier.rates.USD);
  const monthly = qty * rate;
  return period === 'annual' ? Math.round(monthly * 0.85) : monthly;
}

function fmtMoney(amount, currency, period) {
  const sym = CURRENCY_SYMBOL[currency] || '$';
  return `${sym}${amount.toLocaleString()}/${period === 'annual' ? 'mo (annual)' : 'mo'}`;
}

function checkBandCrossing(currentQty, newQty, currency, period) {
  const currentTier = getTier(currentQty);
  const newTier = getTier(newQty);
  if (currentTier.name === newTier.name) return null;
  return {
    currentTier: currentTier.name,
    newTier: newTier.name,
    currentBill: calcMonthlyBill(currentQty, currency, period),
    newBill: calcMonthlyBill(newQty, currency, period),
    currency,
    period,
  };
}

function getHeadroom(effectiveQty) {
  const tier = getTier(effectiveQty);
  if (tier.max === null) return null;
  const remaining = tier.max - effectiveQty;
  const nextTier = getTier(tier.max + 1);
  return { remaining, nextTierName: nextTier.name };
}
// ─────────────────────────────────────────────────────────────────────────────

function SitesManagement() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [createData, setCreateData] = useState({
    name: '',
    slug: '',
    chat_enabled: false,
  });
  const [editingSite, setEditingSite] = useState(null);
  const [editData, setEditData] = useState({ name: '', slug: '', deposit_url: '', deposit_button_label: '' });
  const [busy, setBusy] = useState({});
  const [rotatedSecrets, setRotatedSecrets] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState('');
  const [brandBusy, setBrandBusy] = useState(false);
  const [pricingModal, setPricingModal] = useState(null); // { crossing, onConfirm }
  const [confirmModal, setConfirmModal] = useState(null); // { title, body, onConfirm }

  useEffect(() => {
    loadSites();
    loadBilling();
    loadBrands();
  }, []);

  const loadSites = async () => {
    try {
      const data = await api.getSites();
      setSites(data);
    } catch (error) {
      console.error('Failed to load sites:', error);
      setMessage({ type: 'error', text: 'Failed to load sites' });
    } finally {
      setLoading(false);
    }
  };

  const loadBilling = async () => {
    try {
      const data = await api.getBillingSummary();
      setBilling(data);
    } catch (error) {
      console.error('Failed to load billing:', error);
    }
  };

  const syncBilling = async () => {
    setBillingBusy(true);
    try {
      const data = await api.syncBilling();
      await loadBilling();
      if (data.status === 'incomplete') {
        setMessage({ type: 'error', text: 'Payment method required — click Manage Billing to add a card first.' });
      } else if (data.status === 'free_forever') {
        setMessage({ type: 'success', text: 'Free account — no billing action needed.' });
      } else {
        setMessage({ type: 'success', text: 'Billing synced with Stripe.' });
      }
    } catch (error) {
      console.error('Billing sync failed:', error);
      setMessage({ type: 'error', text: error.message || 'Billing sync failed' });
    } finally {
      setBillingBusy(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const data = await api.getBillingPortal();
      window.open(data.url, '_blank');
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to open billing portal' });
    } finally {
      setPortalBusy(false);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await api.getBrands();
      setBrands(data);
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  };

  // ── Add Brand ──────────────────────────────────────────────────────────────
  const doAddBrand = async (brandName) => {
    setBrandBusy(true);
    try {
      await api.addBrand(brandName);
      setNewBrand('');
      await loadBrands();
      await loadBilling();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to add brand' });
    } finally {
      setBrandBusy(false);
    }
  };

  const handleAddBrand = async (event) => {
    event.preventDefault();
    if (!newBrand.trim()) return;

    if (billing && !billing.free_forever) {
      const currentQty = Math.max(billing.rooftop_count || 0, billing.brand_count || 0);
      const newBrandCount = (billing.brand_count || 0) + 1;
      const newQty = Math.max(billing.rooftop_count || 0, newBrandCount);
      const crossing = checkBandCrossing(currentQty, newQty, billing.billing_currency || 'USD', billing.billing_period || 'monthly');
      if (crossing) {
        setPricingModal({
          crossing,
          what: `adding brand "${newBrand.trim()}"`,
          onConfirm: () => {
            setPricingModal(null);
            doAddBrand(newBrand.trim());
          },
        });
        return;
      }
    }

    await doAddBrand(newBrand.trim());
  };

  const handleRemoveBrand = async (brand) => {
    if (!window.confirm(`Remove brand "${brand.name}"? This will update your billing.`)) return;
    setBrandBusy(true);
    try {
      await api.removeBrand(brand.id);
      await loadBrands();
      await loadBilling();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to remove brand' });
    } finally {
      setBrandBusy(false);
    }
  };

  const setBusyState = (siteId, key, value) => {
    setBusy(prev => ({
      ...prev,
      [siteId]: {
        ...(prev[siteId] || {}),
        [key]: value,
      },
    }));
  };

  function guessChatOrigin() {
    const configured = process.env.REACT_APP_CHAT_BASE_URL;
    if (configured && configured.trim()) return configured.replace(/\/$/, '');
    const host = window.location.host;
    if (host.startsWith('crm.')) return `${window.location.protocol}//chat.${host.slice(4)}`;
    return window.location.origin;
  }

  const buildHostedLink = (siteKey) => `${guessChatOrigin()}/s/${siteKey}`;
  const buildWidgetSnippet = (siteKey) => (
    `<script async src="${guessChatOrigin()}/widget.js" data-site-key="${siteKey}"></script>`
  );

  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: successMessage || 'Copied to clipboard' });
    } catch (error) {
      console.error('Copy failed:', error);
      setMessage({ type: 'error', text: 'Failed to copy to clipboard' });
    }
  };

  // ── Create Site ────────────────────────────────────────────────────────────
  const doCreateSite = async (payload) => {
    try {
      await api.createSite(payload);
      setMessage({ type: 'success', text: 'Site created' });
      setCreateData({ name: '', slug: '', chat_enabled: false });
      await loadSites();
      syncBilling();
    } catch (error) {
      console.error('Failed to create site:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to create site' });
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const payload = {
      name: createData.name.trim(),
      slug: createData.slug.trim() || undefined,
    };
    await doCreateSite(payload);
  };

  const handleToggleLive = async (site) => {
    const goingLive = !site.is_live;

    if (goingLive) {
      // Band-crossing check when going live (billing event)
      if (billing && !billing.free_forever) {
        const currentQty = Math.max(billing.rooftop_count || 0, billing.brand_count || 0);
        const newRooftopCount = (billing.rooftop_count || 0) + 1;
        const newQty = Math.max(newRooftopCount, billing.brand_count || 0);
        const crossing = checkBandCrossing(currentQty, newQty, billing.billing_currency || 'USD', billing.billing_period || 'monthly');
        if (crossing) {
          setPricingModal({
            crossing,
            what: `taking "${site.name}" live`,
            onConfirm: () => {
              setPricingModal(null);
              doToggleLive(site, true);
            },
          });
          return;
        }
      }
      // Standard go-live confirm (no band crossing)
      setConfirmModal({
        title: 'Turn on Go Live?',
        body: `"${site.name}" will go live and billing will begin for this rooftop.`,
        onConfirm: () => {
          setConfirmModal(null);
          doToggleLive(site, true);
        },
      });
    } else {
      // Going dark confirm
      setConfirmModal({
        title: 'Take this site offline?',
        body: `"${site.name}" will go dark immediately. The chat widget will stop accepting leads.`,
        onConfirm: () => {
          setConfirmModal(null);
          doToggleLive(site, false);
        },
      });
    }
  };

  const doToggleLive = async (site, goingLive) => {
    setBusyState(site.id, 'toggle', true);
    try {
      const updated = await api.updateSite(site.id, { is_live: goingLive });
      setSites(prev => prev.map(item => (item.id === site.id ? updated : item)));
      setMessage({ type: 'success', text: `${site.name} is now ${goingLive ? 'live' : 'inactive'}` });
      if (billing && !billing.free_forever) syncBilling();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update site' });
    } finally {
      setBusyState(site.id, 'toggle', false);
    }
  };

  const handleRotateKeys = async (site) => {
    if (!window.confirm(`Rotate keys for ${site.name}? Existing integrations will stop working until updated.`)) {
      return;
    }

    setBusyState(site.id, 'rotate', true);
    try {
      const data = await api.rotateSiteKeys(site.id);
      setRotatedSecrets({
        siteName: site.name,
        public_site_key: data.public_site_key,
        chat_webhook_secret: data.chat_webhook_secret,
      });
      setSites(prev => prev.map(item => (item.id === site.id ? { ...item, public_site_key: data.public_site_key } : item)));
      setMessage({ type: 'success', text: 'Keys rotated' });
    } catch (error) {
      console.error('Failed to rotate keys:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to rotate keys' });
    } finally {
      setBusyState(site.id, 'rotate', false);
    }
  };

  const openEdit = (site) => {
    setEditingSite(site);
    setEditData({ name: site.name, slug: site.slug, deposit_url: site.deposit_url || '', deposit_button_label: site.deposit_button_label || '' });
  };

  const handleEditSave = async (event) => {
    event.preventDefault();
    if (!editingSite) return;

    setBusyState(editingSite.id, 'edit', true);
    try {
      const payload = {
        name: editData.name.trim(),
        slug: editData.slug.trim() || undefined,
        deposit_url: editData.deposit_url.trim() || null,
        deposit_button_label: editData.deposit_button_label.trim() || null,
      };
      const updated = await api.updateSite(editingSite.id, payload);
      setSites(prev => prev.map(item => (item.id === editingSite.id ? updated : item)));
      setMessage({ type: 'success', text: 'Site updated' });
      setEditingSite(null);
    } catch (error) {
      console.error('Failed to update site:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update site' });
    } finally {
      setBusyState(editingSite.id, 'edit', false);
    }
  };

  const hostedLinkCache = useMemo(() => {
    const cache = {};
    sites.forEach(site => {
      cache[site.id] = buildHostedLink(site.public_site_key);
    });
    return cache;
  }, [sites]);

  // ── Headroom calculation ───────────────────────────────────────────────────
  const headroom = useMemo(() => {
    if (!billing || billing.free_forever) return null;
    const effectiveQty = Math.max(billing.rooftop_count || 0, billing.brand_count || 0);
    if (effectiveQty === 0) return null;
    const base = getHeadroom(effectiveQty);
    if (!base) return null;
    const currency = billing.billing_currency || 'USD';
    const period = billing.billing_period || 'monthly';
    const sym = CURRENCY_SYMBOL[currency] || '$';
    const currentTier = getTier(effectiveQty);
    const nextTier = getTier(effectiveQty + base.remaining + 1);
    const currentRate = currentTier.rates[currency] || currentTier.rates.USD;
    const nextRate = nextTier.rates[currency] || nextTier.rates.USD;
    const annualMult = period === 'annual' ? 0.85 : 1;
    const currentBill = Math.round(effectiveQty * currentRate * annualMult);
    // bill at the crossing point = (effectiveQty + remaining + 1) rooftops at next rate
    const crossingQty = effectiveQty + base.remaining + 1;
    const newBill = Math.round(crossingQty * nextRate * annualMult);
    return {
      ...base,
      sym,
      currency,
      period,
      currentRate: Math.round(currentRate * annualMult),
      nextRate: Math.round(nextRate * annualMult),
      currentBill,
      newBill,
      crossingQty,
    };
  }, [billing]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Sites</h1>
          <p style={styles.subtitle}>Manage Group/Site settings, chat availability, and keys</p>
        </div>
      </div>

      {billing && (
        <div style={styles.billingCard}>
          <div style={styles.billingLeft}>
            <div style={styles.billingTier}>
              {billing.free_forever ? 'Free Account' : billing.tier}
            </div>
            {!billing.free_forever && (
              <div style={styles.billingMeta}>
                {billing.rooftop_count} {billing.rooftop_count === 1 ? 'rooftop' : 'rooftops'} live
                {billing.total_site_count > billing.rooftop_count && (
                  <span style={styles.billingNote}> ({billing.total_site_count - billing.rooftop_count} dark)</span>
                )}
                {billing.brand_count > 1 && ` · ${billing.brand_count} brands`}
                {billing.brand_count > billing.rooftop_count && (
                  <span style={styles.billingNote}> (tier set by brand count)</span>
                )}
                {' · '}
                {billing.billing_currency}
                {' · '}
                {billing.billing_period}
                {billing.stripe_subscription_status === 'incomplete' && (
                  <span style={styles.billingWarning}> · Payment required</span>
                )}
              </div>
            )}
            {!billing.free_forever && headroom !== null && (
              <div style={styles.headroomText}>
                {headroom.remaining === 0
                  ? `Next site or brand moves you to ${headroom.nextTierName} — rate changes from ${headroom.sym}${headroom.currentRate} to ${headroom.sym}${headroom.nextRate}/rooftop/mo, total bill ${headroom.sym}${headroom.currentBill} → ${headroom.sym}${headroom.newBill}/mo`
                  : `${headroom.remaining} more ${headroom.remaining === 1 ? 'rooftop/brand' : 'rooftops/brands'} before ${headroom.nextTierName} pricing — rate drops from ${headroom.sym}${headroom.currentRate} to ${headroom.sym}${headroom.nextRate}/rooftop/mo`}
              </div>
            )}
            {billing.free_forever && (
              <div style={styles.billingMeta}>No billing — founder account</div>
            )}
          </div>
          {!billing.free_forever && (
            <div style={styles.billingActions}>
              <button
                type="button"
                onClick={openBillingPortal}
                disabled={portalBusy || !billing.stripe_customer_id}
                style={styles.primaryButton}
              >
                {portalBusy ? 'Opening...' : 'Manage Billing'}
              </button>
              <button
                type="button"
                onClick={syncBilling}
                disabled={billingBusy}
                style={styles.syncButton}
              >
                {billingBusy ? 'Syncing...' : 'Sync'}
              </button>
            </div>
          )}
        </div>
      )}

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            color: message.type === 'success' ? '#86efac' : '#fca5a5',
          }}
        >
          {message.text}
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Brands</h2>
          <p style={styles.cardSubtitle}>
            Brands represented by this dealer group. Billing tier uses the higher of rooftop count or brand count.
          </p>
        </div>
        <form onSubmit={handleAddBrand} style={styles.brandRow}>
          <input
            type="text"
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            style={styles.input}
            placeholder="e.g. Toyota, Ford, BMW"
          />
          <button type="submit" style={styles.primaryButton} disabled={brandBusy || !newBrand.trim()}>
            Add Brand
          </button>
        </form>
        {brands.length > 0 && (
          <div style={styles.brandList}>
            {brands.map(brand => (
              <div key={brand.id} style={styles.brandChip}>
                <span style={styles.brandName}>{brand.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveBrand(brand)}
                  style={styles.brandRemove}
                  disabled={brandBusy}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        {brands.length === 0 && (
          <p style={styles.brandEmpty}>No brands added yet. 1 brand is included in your base plan.</p>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Create Site</h2>
        </div>
        <form onSubmit={handleCreate} style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Name *</label>
            <input
              type="text"
              value={createData.name}
              onChange={(event) => setCreateData({ ...createData, name: event.target.value })}
              style={styles.input}
              placeholder="Vinnie Autos - Downtown"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Slug (optional)</label>
            <input
              type="text"
              value={createData.slug}
              onChange={(event) => setCreateData({ ...createData, slug: event.target.value })}
              style={styles.input}
              placeholder="downtown"
            />
          </div>
          <button type="submit" style={styles.primaryButton}>
            Create Site
          </button>
        </form>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading sites...</div>
      ) : sites.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyText}>No sites yet</div>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Slug</th>
                <th style={styles.th}>Go Live</th>
                <th style={styles.th}>Hosted Chat Link</th>
                <th style={styles.th}>Widget Snippet</th>
                <th style={styles.th}>Keys</th>
              </tr>
            </thead>
            <tbody>
              {sites.map(site => (
                <tr key={site.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.nameCell}>
                      <div style={styles.siteName}>{site.name}</div>
                      <button
                        type="button"
                        onClick={() => openEdit(site)}
                        style={styles.textButton}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.slugText}>{site.slug}</div>
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      onClick={() => handleToggleLive(site)}
                      disabled={busy[site.id]?.toggle}
                      style={{
                        ...styles.liveSwitch,
                        ...(site.is_live ? styles.liveSwitchOn : styles.liveSwitchOff),
                        opacity: busy[site.id]?.toggle ? 0.5 : 1,
                        cursor: busy[site.id]?.toggle ? 'not-allowed' : 'pointer',
                      }}
                      aria-label={site.is_live ? 'Go Live: On' : 'Go Live: Off'}
                    >
                      <span style={{ ...styles.liveSwitchThumb, ...(site.is_live ? styles.liveSwitchThumbOn : styles.liveSwitchThumbOff) }} />
                      <span style={styles.liveSwitchLabel}>{site.is_live ? 'On' : 'Off'}</span>
                    </button>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.copyRow}>
                      <input
                        style={styles.readonlyInput}
                        value={hostedLinkCache[site.id]}
                        readOnly
                      />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(hostedLinkCache[site.id], 'Hosted chat link copied')}
                        style={styles.secondaryButton}
                      >
                        Copy
                      </button>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.copyColumn}>
                      <code style={styles.snippet}>
                        {buildWidgetSnippet(site.public_site_key)}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(buildWidgetSnippet(site.public_site_key), 'Widget snippet copied')}
                        style={styles.secondaryButton}
                      >
                        Copy Snippet
                      </button>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      onClick={() => handleRotateKeys(site)}
                      style={styles.dangerButton}
                      disabled={busy[site.id]?.rotate}
                    >
                      Rotate Keys
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Site Modal */}
      {editingSite && (
        <div style={styles.modalOverlay} onClick={() => setEditingSite(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Site</h2>
            <form onSubmit={handleEditSave}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(event) => setEditData({ ...editData, name: event.target.value })}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Slug</label>
                <input
                  type="text"
                  value={editData.slug}
                  onChange={(event) => setEditData({ ...editData, slug: event.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
                  Deposit Button URL
                </label>
                <input
                  type="url"
                  value={editData.deposit_url}
                  onChange={e => setEditData(prev => ({ ...prev, deposit_url: e.target.value }))}
                  placeholder="https://dealer.com/reserve"
                  style={{ width: '100%', padding: '8px', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                  Link opens in a new tab from the chat widget. Leave blank to hide the button.
                </p>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>
                  Deposit Button Label
                </label>
                <input
                  type="text"
                  value={editData.deposit_button_label}
                  onChange={e => setEditData(prev => ({ ...prev, deposit_button_label: e.target.value }))}
                  placeholder="Place Deposit"
                  maxLength={100}
                  style={{ width: '100%', padding: '8px', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
              <div style={styles.modalButtons}>
                <button
                  type="button"
                  onClick={() => setEditingSite(null)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.primaryButton} disabled={busy[editingSite.id]?.edit}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rotate Keys Modal */}
      {rotatedSecrets && (
        <div style={styles.modalOverlay} onClick={() => setRotatedSecrets(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <h2 style={styles.modalTitle}>Keys Rotated</h2>
            <p style={styles.modalSubtitle}>
              New keys for {rotatedSecrets.siteName}. The secret is only shown once. Store it securely now.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Public Site Key</label>
              <div style={styles.copyRow}>
                <input style={styles.input} value={rotatedSecrets.public_site_key} readOnly />
                <button
                  type="button"
                  onClick={() => copyToClipboard(rotatedSecrets.public_site_key, 'Public site key copied')}
                  style={styles.secondaryButton}
                >
                  Copy
                </button>
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Chat Webhook Secret</label>
              <div style={styles.copyRow}>
                <input style={styles.input} value={rotatedSecrets.chat_webhook_secret} readOnly />
                <button
                  type="button"
                  onClick={() => copyToClipboard(rotatedSecrets.chat_webhook_secret, 'Webhook secret copied')}
                  style={styles.secondaryButton}
                >
                  Copy
                </button>
              </div>
            </div>
            <button type="button" onClick={() => setRotatedSecrets(null)} style={styles.primaryButton}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Pricing Band Crossing Modal */}
      {confirmModal && (
        <div style={styles.modalOverlay} onClick={() => setConfirmModal(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <h2 style={styles.modalTitle}>{confirmModal.title}</h2>
            <p style={styles.modalSubtitle}>{confirmModal.body}</p>
            <div style={styles.modalButtons}>
              <button
                type="button"
                onClick={() => setConfirmModal(null)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmModal.onConfirm}
                style={styles.primaryButton}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {pricingModal && (
        <div style={styles.modalOverlay} onClick={() => setPricingModal(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <h2 style={styles.modalTitle}>Pricing tier change</h2>
            <p style={styles.modalSubtitle}>
              {pricingModal.what} will move your account to a new pricing tier.
            </p>
            <div style={styles.tierCompareBox}>
              <div style={styles.tierCompareRow}>
                <div style={styles.tierCompareLabel}>Current tier</div>
                <div style={styles.tierCompareCurrent}>{pricingModal.crossing.currentTier}</div>
                <div style={styles.tierCompareAmount}>
                  {fmtMoney(pricingModal.crossing.currentBill, pricingModal.crossing.currency, pricingModal.crossing.period)}
                </div>
              </div>
              <div style={styles.tierCompareArrow}>↓</div>
              <div style={{ ...styles.tierCompareRow, ...styles.tierCompareRowNew }}>
                <div style={styles.tierCompareLabel}>New tier</div>
                <div style={styles.tierCompareNew}>{pricingModal.crossing.newTier}</div>
                <div style={styles.tierCompareAmountNew}>
                  {fmtMoney(pricingModal.crossing.newBill, pricingModal.crossing.currency, pricingModal.crossing.period)}
                </div>
              </div>
            </div>
            <p style={styles.tierCompareNote}>
              {pricingModal.crossing.newBill > pricingModal.crossing.currentBill
                ? `Your bill will increase by ${CURRENCY_SYMBOL[pricingModal.crossing.currency] || '$'}${(pricingModal.crossing.newBill - pricingModal.crossing.currentBill).toLocaleString()}/mo. The per-rooftop rate decreases at higher tiers.`
                : `Your per-rooftop rate decreases at this tier. Your bill will be ${CURRENCY_SYMBOL[pricingModal.crossing.currency] || '$'}${(pricingModal.crossing.currentBill - pricingModal.crossing.newBill).toLocaleString()}/mo less.`}
            </p>
            <div style={styles.modalButtons}>
              <button
                type="button"
                onClick={() => setPricingModal(null)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={pricingModal.onConfirm}
                style={styles.primaryButton}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: 0,
    fontSize: '14px',
  },
  billingCard: {
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    padding: '16px 20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  billingLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  billingActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  billingTier: {
    color: '#f1f5f9',
    fontSize: '15px',
    fontWeight: '600',
  },
  billingMeta: {
    color: '#94a3b8',
    fontSize: '13px',
  },
  billingNote: {
    color: '#fbbf24',
    fontSize: '12px',
  },
  billingWarning: {
    color: '#ef4444',
    fontSize: '12px',
    fontWeight: '600',
  },
  headroomText: {
    color: '#64748b',
    fontSize: '12px',
    marginTop: '2px',
  },
  syncButton: {
    padding: '8px 16px',
    background: '#1d4ed8',
    color: '#f1f5f9',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  brandRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '16px',
  },
  brandList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  brandChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '20px',
    padding: '6px 12px',
  },
  brandName: {
    color: '#f1f5f9',
    fontSize: '13px',
  },
  brandRemove: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '0',
  },
  brandEmpty: {
    color: '#64748b',
    fontSize: '13px',
    margin: '8px 0 0 0',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  card: {
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    padding: '20px',
    marginBottom: '24px',
  },
  cardHeader: {
    marginBottom: '16px',
  },
  cardTitle: {
    color: '#f1f5f9',
    margin: '0 0 6px 0',
    fontSize: '18px',
    fontWeight: '600',
  },
  cardSubtitle: {
    color: '#94a3b8',
    margin: 0,
    fontSize: '13px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 1.4fr) minmax(200px, 1fr) auto auto',
    gap: '16px',
    alignItems: 'end',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  readonlyInput: {
    width: '100%',
    padding: '8px 10px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#94a3b8',
    fontSize: '12px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  checkbox: {
    accentColor: 'var(--brand-primary, #3b82f6)',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#94a3b8',
    fontSize: '13px',
  },
  primaryButton: {
    padding: '10px 18px',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '8px 12px',
    background: '#0f172a',
    color: '#e2e8f0',
    border: '1px solid #334155',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dangerButton: {
    padding: '8px 12px',
    background: 'transparent',
    color: '#fca5a5',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  textButton: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#60a5fa',
    fontSize: '12px',
    cursor: 'pointer',
  },
  liveSwitch: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    width: '64px',
    height: '28px',
    borderRadius: '999px',
    border: 'none',
    padding: '3px 6px 3px 3px',
    position: 'relative',
    transition: 'background 0.2s',
    fontFamily: "'Noto Sans', sans-serif",
  },
  liveSwitchOn: {
    background: 'rgba(34, 197, 94, 0.25)',
    color: '#86efac',
    justifyContent: 'flex-end',
  },
  liveSwitchOff: {
    background: 'rgba(148, 163, 184, 0.2)',
    color: '#94a3b8',
    justifyContent: 'flex-start',
  },
  liveSwitchThumb: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'background 0.2s',
  },
  liveSwitchThumbOn: {
    background: '#86efac',
  },
  liveSwitchThumbOff: {
    background: '#94a3b8',
  },
  liveSwitchLabel: {
    fontSize: '11px',
    fontWeight: '600',
    lineHeight: 1,
    flexShrink: 0,
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
    textAlign: 'center',
    padding: '48px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 32px',
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '18px',
  },
  tableContainer: {
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '16px',
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #334155',
    background: '#0f172a',
  },
  tr: {
    borderBottom: '1px solid #334155',
  },
  td: {
    padding: '16px',
    verticalAlign: 'top',
  },
  nameCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  siteName: {
    color: '#f1f5f9',
    fontSize: '14px',
    fontWeight: '600',
  },
  slugText: {
    color: '#cbd5f5',
    fontSize: '13px',
  },
  copyRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  copyColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  snippet: {
    display: 'block',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    padding: '8px',
    fontSize: '11px',
    color: '#e2e8f0',
    wordBreak: 'break-all',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '28px',
    width: '100%',
    maxWidth: '520px',
    border: '1px solid #334155',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  modalTitle: {
    color: '#f1f5f9',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  modalSubtitle: {
    color: '#94a3b8',
    fontSize: '13px',
    margin: 0,
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '10px 16px',
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
  },
  tierCompareBox: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  tierCompareRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  tierCompareRowNew: {
    marginTop: '2px',
  },
  tierCompareLabel: {
    color: '#64748b',
    fontSize: '12px',
    width: '72px',
    flexShrink: 0,
  },
  tierCompareCurrent: {
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '600',
    flex: 1,
  },
  tierCompareNew: {
    color: '#60a5fa',
    fontSize: '14px',
    fontWeight: '600',
    flex: 1,
  },
  tierCompareAmount: {
    color: '#94a3b8',
    fontSize: '13px',
    textAlign: 'right',
  },
  tierCompareAmountNew: {
    color: '#f1f5f9',
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'right',
  },
  tierCompareArrow: {
    color: '#475569',
    fontSize: '14px',
    paddingLeft: '84px',
  },
  tierCompareNote: {
    color: '#64748b',
    fontSize: '12px',
    margin: 0,
    lineHeight: '1.5',
  },
};

export default SitesManagement;
